import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Plus, Users, Target, TrendingUp, Calendar, X, Loader2, User, Phone, Mail, Building2, MapPin, MessageSquare, Upload, ExternalLink, ChevronLeft, ChevronRight, Star, Zap, DollarSign, CheckCircle, Clock, Award, Link, UserPlus, Copy, MousePointerClick, Trash2 } from 'lucide-react';
import api, { Prospect, ProspectDetail, ProspectList, ProspectStats, ProspectImport, ProspectTouch, EnrichmentJob, EnrichmentExtractedData, ProspectContact, TrackableLink, EmailCampaign } from '../services/api';

// New workflow statuses
const STATUSES = [
  { value: 'new', label: 'New', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200' },
  { value: 'enriching', label: 'Enriching', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  { value: 'scored', label: 'Scored', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
  { value: 'queued', label: 'Queued', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  { value: 'contacting', label: 'Contacting', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300' },
  { value: 'nurture', label: 'Nurture', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300' },
  { value: 'won', label: 'Won', color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
  { value: 'lost', label: 'Lost', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
];

const VERTICALS = [
  { value: 'roofing', label: 'Roofing' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'painting', label: 'Painting' },
  { value: 'flooring', label: 'Flooring' },
  { value: 'kitchen_bath', label: 'Kitchen & Bath' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'general_contractor', label: 'General Contractor' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'wealth_management', label: 'Wealth Management' },
  { value: 'cpa', label: 'CPA' },
  { value: 'other', label: 'Other' },
];

const TOUCH_CHANNELS = [
  { value: 'call', label: 'Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
  { value: 'linkedin', label: 'LinkedIn', icon: MessageSquare },
  { value: 'mail', label: 'Direct Mail', icon: Mail },
];

const TOUCH_RESULTS = [
  { value: 'pending', label: 'Pending' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'left_vm', label: 'Left Voicemail' },
  { value: 'replied', label: 'Replied' },
  { value: 'booked', label: 'Booked Meeting' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'bad_data', label: 'Bad Data' },
];

const ACTIVITY_TYPES = [
  { value: 'call', label: 'Call', icon: Phone },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'linkedin', label: 'LinkedIn', icon: MessageSquare },
  { value: 'meeting', label: 'Meeting', icon: Calendar },
  { value: 'note', label: 'Note', icon: MessageSquare },
  { value: 'research', label: 'Research', icon: Search },
];

const PAGE_SIZE = 50;

// Helper to get tier badge color
const getTierColor = (tier: number) => {
  switch (tier) {
    case 1: return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 2: return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
    default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
  }
};

// Helper to get score color
const getScoreColor = (score: number) => {
  if (score >= 75) return 'text-green-600';
  if (score >= 55) return 'text-amber-600';
  return 'text-gray-400';
};

const ProspectsPage: React.FC = () => {
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [prospectLists, setProspectLists] = useState<ProspectList[]>([]);
  const [stats, setStats] = useState<ProspectStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(''); // What user types
  const [searchQuery, setSearchQuery] = useState(''); // Debounced value used for API
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedList, setSelectedList] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedVertical, setSelectedVertical] = useState('all');
  const [selectedTier, setSelectedTier] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedConfidence, setSelectedConfidence] = useState('all');
  const [selectedSignal, setSelectedSignal] = useState('all');
  const [selectedEnriched, setSelectedEnriched] = useState('all');
  const [sortField, setSortField] = useState('-created_at');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Debounce search input
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 400); // 400ms debounce
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Detail panel
  const [selectedProspect, setSelectedProspect] = useState<ProspectDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<ProspectDetail>>({});
  const [savingEdit, setSavingEdit] = useState(false);

  // Enrichment state
  const [enriching, setEnriching] = useState(false);
  const [enrichmentJobId, setEnrichmentJobId] = useState<string | null>(null);
  const [enrichmentJob, setEnrichmentJob] = useState<EnrichmentJob | null>(null);
  const [showEnrichmentReview, setShowEnrichmentReview] = useState(false);
  const [enrichmentReviewData, setEnrichmentReviewData] = useState<EnrichmentExtractedData>({});
  const [approvingEnrichment, setApprovingEnrichment] = useState(false);

  // Import modal
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importListId, setImportListId] = useState<string>('');
  const [importing, setImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<ProspectImport | null>(null);

  // New list modal
  const [showNewListModal, setShowNewListModal] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListSource, setNewListSource] = useState('');

  // Scrap.io import modal (uses export-based workflow)
  const [showScrapioModal, setShowScrapioModal] = useState(false);
  const [scrapioBusinessType, setScrapioBusinessType] = useState('roofing-contractor');
  const [scrapioImportMode, setScrapioImportMode] = useState<'city' | 'state'>('city');
  const [scrapioCity, setScrapioCity] = useState('');
  const [scrapioStateCode, setScrapioStateCode] = useState('');
  const [scrapioMaxResults, setScrapioMaxResults] = useState('100');
  const [scrapioMaxResultsPerCity, setScrapioMaxResultsPerCity] = useState('50');
  const [scrapioMaxCities, setScrapioMaxCities] = useState('');
  const [scrapioListName, setScrapioListName] = useState('');
  const [scrapioListId, setScrapioListId] = useState('');
  const [scrapioImporting, setScrapioImporting] = useState(false);
  const [scrapioImportStatus, setScrapioImportStatus] = useState<ProspectImport | null>(null);
  const [scrapioCredits, setScrapioCredits] = useState<number | null>(null);
  const [scrapioBusinessTypes, setScrapioBusinessTypes] = useState<Record<string, string>>({});
  const [scrapioUsStates, setScrapioUsStates] = useState<Array<{ code: string; name: string; city_count: number }>>([]);
  const [scrapioCityError, setScrapioCityError] = useState('');

  // New prospect modal
  const [showNewProspectModal, setShowNewProspectModal] = useState(false);
  const [newProspect, setNewProspect] = useState({
    prospect_list: '',
    company_name: '',
    vertical: 'other',
    owner_name: '',
    owner_email: '',
    phone: '',
    website: '',
    city: '',
    state: '',
    notes: '',
  });
  const [savingProspect, setSavingProspect] = useState(false);

  // Activity form
  const [newActivityType, setNewActivityType] = useState('note');
  const [newActivityDescription, setNewActivityDescription] = useState('');
  const [savingActivity, setSavingActivity] = useState(false);

  // Touch form
  const [newTouchChannel, setNewTouchChannel] = useState('call');
  const [newTouchResult, setNewTouchResult] = useState('pending');
  const [newTouchNotes, setNewTouchNotes] = useState('');
  const [savingTouch, setSavingTouch] = useState(false);

  // Contacts state
  const [prospectContacts, setProspectContacts] = useState<ProspectContact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [newContact, setNewContact] = useState({
    first_name: '',
    last_name: '',
    title: '',
    title_category: 'owner',
    email: '',
    phone: '',
    linkedin_url: '',
    is_decision_maker: true,
  });
  const [savingContact, setSavingContact] = useState(false);

  // Trackable Links state
  const [prospectLinks, setProspectLinks] = useState<TrackableLink[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [showAddLinkForm, setShowAddLinkForm] = useState(false);
  const [newLink, setNewLink] = useState({
    target_url: '',
    campaign: 'outreach',
    notes: '',
  });
  const [savingLink, setSavingLink] = useState(false);

  // Email Campaign enrollment state
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [emailCampaigns, setEmailCampaigns] = useState<EmailCampaign[]>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>('');
  const [enrolling, setEnrolling] = useState(false);
  const [enrollOnlyDecisionMakers, setEnrollOnlyDecisionMakers] = useState(true);

  // Get unique categories and cities from prospects
  const categories = Array.from(new Set(prospects.map(p => p.category).filter(Boolean)));
  const cities = Array.from(new Set(prospects.map(p => p.city).filter(Boolean))).sort();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [prospectsData, listsData, statsData] = await Promise.all([
        api.getProspects({
          ...(selectedList !== 'all' && { prospect_list: selectedList }),
          ...(selectedStatus !== 'all' && { status: selectedStatus }),
          ...(selectedVertical !== 'all' && { vertical: selectedVertical }),
          ...(selectedTier !== 'all' && { tier: selectedTier }),
          ...(selectedCity !== 'all' && { city: selectedCity }),
          ...(selectedConfidence !== 'all' && { confidence: selectedConfidence }),
          ...(selectedSignal !== 'all' && { [selectedSignal]: 'true' }),
          ...(selectedEnriched !== 'all' && { enriched: selectedEnriched }),
          ...(searchQuery && { search: searchQuery }),
          ordering: sortField,
        }),
        api.getProspectLists(),
        api.getProspectStats({
          ...(selectedList !== 'all' && { prospect_list: selectedList }),
          ...(selectedStatus !== 'all' && { status: selectedStatus }),
          ...(selectedVertical !== 'all' && { vertical: selectedVertical }),
          ...(selectedTier !== 'all' && { tier: selectedTier }),
          ...(selectedCity !== 'all' && { city: selectedCity }),
          ...(selectedConfidence !== 'all' && { confidence: selectedConfidence }),
          ...(selectedSignal !== 'all' && { [selectedSignal]: 'true' }),
          ...(selectedEnriched !== 'all' && { enriched: selectedEnriched }),
          ...(searchQuery && { search: searchQuery }),
        }),
      ]);
      // Handle both array and paginated response formats
      setProspects(Array.isArray(prospectsData) ? prospectsData : (prospectsData as any).results || []);
      setProspectLists(Array.isArray(listsData) ? listsData : (listsData as any).results || []);
      setStats(statsData);
      setCurrentPage(1); // Reset to first page when data changes
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prospects');
    } finally {
      setLoading(false);
    }
  }, [selectedList, selectedStatus, selectedVertical, selectedTier, selectedCity, selectedConfidence, selectedSignal, selectedEnriched, searchQuery, sortField]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch Scrap.io metadata when modal opens
  useEffect(() => {
    if (showScrapioModal) {
      // Fetch business types, states, and credits
      api.getScrapioBusinessTypes().then(result => setScrapioBusinessTypes(result.types)).catch(console.error);
      api.getScrapioUsStates().then(result => setScrapioUsStates(result.states)).catch(console.error);
      api.getScrapioAccountInfo().then(result => setScrapioCredits(result.credits_remaining)).catch(console.error);
    }
  }, [showScrapioModal]);

  // Poll Scrap.io import status
  useEffect(() => {
    if (!scrapioImportStatus || scrapioImportStatus.status === 'completed' || scrapioImportStatus.status === 'failed') {
      return;
    }

    const interval = setInterval(async () => {
      try {
        const status = await api.getProspectImportStatus(scrapioImportStatus.id);
        setScrapioImportStatus(status);
        if (status.status === 'completed' || status.status === 'failed') {
          fetchData();
        }
      } catch (err) {
        console.error('Failed to check import status:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [scrapioImportStatus, fetchData]);

  const handleScrapioImport = async () => {
    // Validate required fields based on mode
    if (scrapioImportMode === 'city' && !scrapioCity.trim()) {
      setScrapioCityError('City is required for city import');
      return;
    }
    if (scrapioImportMode === 'state' && !scrapioStateCode) {
      setScrapioCityError('State is required for state import');
      return;
    }
    if (!scrapioListName && !scrapioListId) {
      return;
    }

    setScrapioCityError('');
    setScrapioImporting(true);
    try {
      const params: Parameters<typeof api.scrapioStartImport>[0] = {
        prospect_list_id: scrapioListId || undefined,
        list_name: scrapioListName || undefined,
        business_type: scrapioBusinessType,
      };

      if (scrapioImportMode === 'city') {
        params.city = scrapioCity.trim();
        params.max_results = parseInt(scrapioMaxResults) || undefined;
      } else {
        params.state_code = scrapioStateCode;
        params.max_results_per_city = parseInt(scrapioMaxResultsPerCity) || undefined;
        params.max_cities = parseInt(scrapioMaxCities) || undefined;
      }

      const result = await api.scrapioStartImport(params);
      // Start polling for status
      setScrapioImportStatus({ id: result.import_id, status: 'processing' } as ProspectImport);
    } catch (err: any) {
      console.error('Failed to start import:', err);
      setScrapioCityError(err.message || 'Failed to start import');
    } finally {
      setScrapioImporting(false);
    }
  };

  const handleSelectProspect = async (prospect: Prospect) => {
    setLoadingDetail(true);
    setProspectContacts([]);
    setProspectLinks([]);
    try {
      const detail = await api.getProspect(prospect.id);
      setSelectedProspect(detail);
      // Load contacts and links in parallel
      fetchContactsAndLinks(prospect.id);
    } catch (err) {
      console.error('Failed to load prospect detail:', err);
    } finally {
      setLoadingDetail(false);
    }
  };

  const fetchContactsAndLinks = async (prospectId: string) => {
    setLoadingContacts(true);
    setLoadingLinks(true);
    try {
      const [contacts, links] = await Promise.all([
        api.getProspectContacts(prospectId),
        api.getTrackableLinks({ prospect: prospectId }),
      ]);
      setProspectContacts(contacts);
      setProspectLinks(links);
    } catch (err) {
      console.error('Failed to load contacts/links:', err);
    } finally {
      setLoadingContacts(false);
      setLoadingLinks(false);
    }
  };

  const handleStatusChange = async (prospectId: string, newStatus: string) => {
    try {
      await api.updateProspectStatus(prospectId, newStatus);
      fetchData();
      if (selectedProspect?.id === prospectId) {
        setSelectedProspect({ ...selectedProspect, status: newStatus as ProspectDetail['status'] });
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const handleConvertToDeal = async () => {
    if (!selectedProspect) return;
    try {
      const result = await api.convertProspectToDeal(selectedProspect.id);
      fetchData();
      setSelectedProspect({ ...selectedProspect, converted_to_deal: result.deal_id, deal_id: result.deal_id });
    } catch (err) {
      console.error('Failed to convert to deal:', err);
    }
  };

  const handleAddActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProspect || !newActivityDescription.trim()) return;

    setSavingActivity(true);
    try {
      await api.createProspectActivity(selectedProspect.id, {
        activity_type: newActivityType,
        description: newActivityDescription,
      });
      const detail = await api.getProspect(selectedProspect.id);
      setSelectedProspect(detail);
      setNewActivityDescription('');
    } catch (err) {
      console.error('Failed to add activity:', err);
    } finally {
      setSavingActivity(false);
    }
  };

  const handleAddTouch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProspect) return;

    setSavingTouch(true);
    try {
      await api.createProspectTouch(selectedProspect.id, {
        channel: newTouchChannel,
        result: newTouchResult,
        notes: newTouchNotes,
      });
      const detail = await api.getProspect(selectedProspect.id);
      setSelectedProspect(detail);
      setNewTouchNotes('');
      setNewTouchResult('pending');
      fetchData(); // Refresh to update touch_count
    } catch (err) {
      console.error('Failed to add touch:', err);
    } finally {
      setSavingTouch(false);
    }
  };

  // Contact handlers
  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProspect || !newContact.first_name.trim()) return;

    setSavingContact(true);
    try {
      await api.createProspectContact(selectedProspect.id, {
        first_name: newContact.first_name,
        last_name: newContact.last_name,
        title: newContact.title,
        title_category: newContact.title_category,
        email: newContact.email,
        phone: newContact.phone,
        linkedin_url: newContact.linkedin_url,
        is_decision_maker: newContact.is_decision_maker,
      });
      const contacts = await api.getProspectContacts(selectedProspect.id);
      setProspectContacts(contacts);
      setShowAddContactForm(false);
      setNewContact({
        first_name: '',
        last_name: '',
        title: '',
        title_category: 'owner',
        email: '',
        phone: '',
        linkedin_url: '',
        is_decision_maker: true,
      });
    } catch (err) {
      console.error('Failed to add contact:', err);
    } finally {
      setSavingContact(false);
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!selectedProspect) return;
    if (!window.confirm('Delete this contact?')) return;

    try {
      await api.deleteContact(contactId);
      const contacts = await api.getProspectContacts(selectedProspect.id);
      setProspectContacts(contacts);
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  const handleSetPrimaryContact = async (contactId: string) => {
    if (!selectedProspect) return;

    try {
      await api.setContactPrimary(contactId);
      const contacts = await api.getProspectContacts(selectedProspect.id);
      setProspectContacts(contacts);
    } catch (err) {
      console.error('Failed to set primary contact:', err);
    }
  };

  // Trackable Link handlers
  const handleAddLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProspect || !newLink.target_url.trim()) return;

    setSavingLink(true);
    try {
      await api.createTrackableLink({
        target_url: newLink.target_url,
        prospect_id: selectedProspect.id,
        campaign: newLink.campaign,
        notes: newLink.notes,
      });
      const links = await api.getTrackableLinks({ prospect: selectedProspect.id });
      setProspectLinks(links);
      setShowAddLinkForm(false);
      setNewLink({
        target_url: '',
        campaign: 'outreach',
        notes: '',
      });
    } catch (err) {
      console.error('Failed to create link:', err);
    } finally {
      setSavingLink(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Email Campaign enrollment
  const handleOpenEnrollModal = async () => {
    setShowEnrollModal(true);
    try {
      const campaigns = await api.getEmailCampaigns();
      setEmailCampaigns(campaigns);
    } catch (err) {
      console.error('Failed to fetch campaigns:', err);
    }
  };

  const handleEnrollInCampaign = async () => {
    if (!selectedProspect || !selectedCampaignId) return;

    // Filter contacts based on decision maker preference
    const contactsToEnroll = enrollOnlyDecisionMakers
      ? prospectContacts.filter(c => c.is_decision_maker && c.email)
      : prospectContacts.filter(c => c.email);

    if (contactsToEnroll.length === 0) {
      window.alert('No contacts with email addresses to enroll. Please add contacts with emails first.');
      return;
    }

    setEnrolling(true);
    try {
      const contactIds = contactsToEnroll.map(c => c.id);
      await api.enrollContactsInCampaign(selectedCampaignId, contactIds);
      setShowEnrollModal(false);
      setSelectedCampaignId('');
      // Refresh contacts to show updated enrollment status
      const contacts = await api.getProspectContacts(selectedProspect.id);
      setProspectContacts(contacts);
      // Update prospect status if currently in "queued" state
      if (selectedProspect.status === 'queued' || selectedProspect.status === 'scored') {
        await api.updateProspectStatus(selectedProspect.id, 'contacting');
        const detail = await api.getProspect(selectedProspect.id);
        setSelectedProspect(detail);
        fetchData();
      }
    } catch (err: any) {
      console.error('Failed to enroll in campaign:', err);
      window.alert(err.message || 'Failed to enroll contacts in campaign');
    } finally {
      setEnrolling(false);
    }
  };

  const handleCalculateScore = async () => {
    if (!selectedProspect) return;
    try {
      const result = await api.calculateProspectScore(selectedProspect.id);
      const detail = await api.getProspect(selectedProspect.id);
      setSelectedProspect(detail);
      fetchData();
    } catch (err) {
      console.error('Failed to calculate score:', err);
    }
  };

  const handleCreateProspect = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProspect.prospect_list || !newProspect.company_name.trim()) return;

    setSavingProspect(true);
    try {
      await api.createProspect({
        prospect_list: newProspect.prospect_list,
        name: newProspect.company_name,
        company_name: newProspect.company_name,
        vertical: newProspect.vertical,
        owner_name: newProspect.owner_name,
        owner_email: newProspect.owner_email,
        phone: newProspect.phone,
        website: newProspect.website,
        city: newProspect.city,
        state: newProspect.state,
        notes: newProspect.notes,
        status: 'new',
      });
      setShowNewProspectModal(false);
      setNewProspect({
        prospect_list: '',
        company_name: '',
        vertical: 'other',
        owner_name: '',
        owner_email: '',
        phone: '',
        website: '',
        city: '',
        state: '',
        notes: '',
      });
      fetchData();
    } catch (err) {
      console.error('Failed to create prospect:', err);
    } finally {
      setSavingProspect(false);
    }
  };

  const handleStartEdit = () => {
    if (!selectedProspect) return;
    setEditForm({
      company_name: selectedProspect.company_name || selectedProspect.name,
      vertical: selectedProspect.vertical,
      owner_name: selectedProspect.owner_name,
      owner_email: selectedProspect.owner_email,
      phone: selectedProspect.phone,
      website: selectedProspect.website,
      city: selectedProspect.city,
      state: selectedProspect.state,
      notes: selectedProspect.notes,
      google_business_url: selectedProspect.google_business_url,
      // Signal fields
      has_financing: selectedProspect.has_financing,
      financing_provider: selectedProspect.financing_provider,
      has_marketing_tools: selectedProspect.has_marketing_tools,
      review_count: selectedProspect.review_count,
      review_rating: selectedProspect.review_rating,
      runs_google_ads: selectedProspect.runs_google_ads,
      has_branded_fleet: selectedProspect.has_branded_fleet,
      is_enterprise: selectedProspect.is_enterprise,
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const handleSaveEdit = async () => {
    if (!selectedProspect) return;
    setSavingEdit(true);
    try {
      await api.updateProspect(selectedProspect.id, {
        ...editForm,
        name: editForm.company_name, // Keep name in sync
      });
      const detail = await api.getProspect(selectedProspect.id);
      setSelectedProspect(detail);
      setIsEditing(false);
      setEditForm({});
      fetchData();
    } catch (err) {
      console.error('Failed to save prospect:', err);
    } finally {
      setSavingEdit(false);
    }
  };

  // Enrichment handlers
  const handleStartEnrichment = async () => {
    if (!selectedProspect) return;
    setEnriching(true);
    try {
      const result = await api.startEnrichment(selectedProspect.id);
      setEnrichmentJobId(result.job_id);

      // Update prospect status locally
      setSelectedProspect({ ...selectedProspect, status: 'enriching' });

      // Start polling for job status
      pollEnrichmentJob(result.job_id);
    } catch (err) {
      console.error('Failed to start enrichment:', err);
      setEnriching(false);
    }
  };

  const pollEnrichmentJob = async (jobId: string) => {
    try {
      const job = await api.getEnrichmentJob(jobId);
      setEnrichmentJob(job);

      if (job.status === 'review') {
        // Job is ready for review
        setEnriching(false);
        setEnrichmentReviewData(job.extracted_data);
        setShowEnrichmentReview(true);
      } else if (job.status === 'failed') {
        setEnriching(false);
        console.error('Enrichment failed:', job.error_message);
      } else if (job.status === 'pending' || job.status === 'searching' || job.status === 'extracting') {
        // Still processing, poll again
        setTimeout(() => pollEnrichmentJob(jobId), 2000);
      }
    } catch (err) {
      console.error('Failed to poll enrichment job:', err);
      setEnriching(false);
    }
  };

  const handleApproveEnrichment = async () => {
    if (!enrichmentJobId) return;
    setApprovingEnrichment(true);
    try {
      const result = await api.approveEnrichment(enrichmentJobId, enrichmentReviewData);
      setSelectedProspect(result.prospect);
      setShowEnrichmentReview(false);
      setEnrichmentJobId(null);
      setEnrichmentJob(null);
      setEnrichmentReviewData({});
      fetchData();
    } catch (err) {
      console.error('Failed to approve enrichment:', err);
    } finally {
      setApprovingEnrichment(false);
    }
  };

  const handleRejectEnrichment = async () => {
    if (!enrichmentJobId) return;
    try {
      await api.rejectEnrichment(enrichmentJobId);
      setShowEnrichmentReview(false);
      setEnrichmentJobId(null);
      setEnrichmentJob(null);
      setEnrichmentReviewData({});
      // Refresh prospect data
      if (selectedProspect) {
        const detail = await api.getProspect(selectedProspect.id);
        setSelectedProspect(detail);
      }
      fetchData();
    } catch (err) {
      console.error('Failed to reject enrichment:', err);
    }
  };

  const handleImport = async () => {
    if (!importFile || !importListId) return;

    setImporting(true);
    try {
      const result = await api.importProspectsCSV(importListId, importFile);
      setImportStatus({ id: result.import_id, status: 'processing', total_rows: result.total_rows } as ProspectImport);

      // Poll for status
      const pollStatus = async () => {
        const status = await api.getProspectImportStatus(result.import_id);
        setImportStatus(status);
        if (status.status === 'processing' || status.status === 'pending') {
          setTimeout(pollStatus, 2000);
        } else {
          fetchData();
        }
      };
      pollStatus();
    } catch (err) {
      console.error('Import failed:', err);
    } finally {
      setImporting(false);
    }
  };

  const handleCreateList = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const newList = await api.createProspectList({ name: newListName, source: newListSource });
      setProspectLists([...prospectLists, newList]);
      setShowNewListModal(false);
      setNewListName('');
      setNewListSource('');
      setImportListId(newList.id);
      // Also set the new list in newProspect for Add Prospect flow
      setNewProspect(prev => ({ ...prev, prospect_list: newList.id }));
      // Reopen Add Prospect modal if user came from there
      setShowNewProspectModal(true);
    } catch (err) {
      console.error('Failed to create list:', err);
    }
  };

  const getStatusInfo = (status: string) => {
    return STATUSES.find(s => s.value === status) || STATUSES[0];
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // Filter prospects by category
  const filteredProspects = selectedCategory === 'all'
    ? prospects
    : prospects.filter(p => p.category === selectedCategory);

  // Pagination
  const totalPages = Math.ceil(filteredProspects.length / PAGE_SIZE);
  const paginatedProspects = filteredProspects.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Prospects</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage outreach and networking prospects</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowScrapioModal(true)}
            className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 flex items-center gap-2"
          >
            <MapPin className="w-4 h-4" />
            Google Maps Import
          </button>
          <button
            onClick={() => setShowImportModal(true)}
            className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
          >
            <Upload className="w-4 h-4" />
            Import CSV
          </button>
          <button
            onClick={() => setShowNewProspectModal(true)}
            className="px-3 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Prospect
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
              <Users className="w-4 h-4" />
              Total
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <Award className="w-4 h-4" />
              Tier 1
            </div>
            <div className="text-2xl font-bold text-green-600">
              {stats.by_tier?.find(t => t.tier === 1)?.count || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center gap-2 text-amber-600 text-sm">
              <Star className="w-4 h-4" />
              Tier 2
            </div>
            <div className="text-2xl font-bold text-amber-600">
              {stats.by_tier?.find(t => t.tier === 2)?.count || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
              <Target className="w-4 h-4" />
              Avg Score
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.avg_score || 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center gap-2 text-cyan-600 text-sm">
              <Zap className="w-4 h-4" />
              Touches (7d)
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.recent_touches || 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center gap-2 text-green-600 text-sm">
              <CheckCircle className="w-4 h-4" />
              Won
            </div>
            <div className="text-2xl font-bold text-green-600">{stats.converted || 0}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center gap-2 text-amber-600 text-sm">
              <Clock className="w-4 h-4" />
              Due Today
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.today_followups}</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <Calendar className="w-4 h-4" />
              Overdue
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats.overdue_actions || stats.overdue_followups}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search prospects..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <select
          value={selectedList}
          onChange={(e) => setSelectedList(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Lists</option>
          {prospectLists.map(list => (
            <option key={list.id} value={list.id}>{list.name} ({list.prospect_count})</option>
          ))}
        </select>
        <select
          value={selectedVertical}
          onChange={(e) => setSelectedVertical(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Verticals</option>
          {VERTICALS.map(v => (
            <option key={v.value} value={v.value}>{v.label}</option>
          ))}
        </select>
        <select
          value={selectedTier}
          onChange={(e) => setSelectedTier(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Tiers</option>
          <option value="1">Tier 1 (75+)</option>
          <option value="2">Tier 2 (55-74)</option>
          <option value="3">Tier 3 (&lt;55)</option>
        </select>
        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Statuses</option>
          {STATUSES.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Categories ({categories.length})</option>
          {categories.sort().map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <select
          value={selectedCity}
          onChange={(e) => setSelectedCity(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Cities ({cities.length})</option>
          {cities.map(city => (
            <option key={city} value={city}>{city}</option>
          ))}
        </select>
        <select
          value={selectedConfidence}
          onChange={(e) => setSelectedConfidence(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Confidence</option>
          <option value="high">High Confidence</option>
          <option value="medium">Medium Confidence</option>
          <option value="low">Low Confidence</option>
        </select>
        <select
          value={selectedSignal}
          onChange={(e) => setSelectedSignal(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All Signals</option>
          <option value="has_financing">Has Financing</option>
          <option value="runs_google_ads">Runs Google Ads</option>
          <option value="has_marketing_tools">Has Marketing Tools</option>
          <option value="has_branded_fleet">Has Branded Fleet</option>
          <option value="hiring_signals">Hiring</option>
        </select>
        <select
          value={selectedEnriched}
          onChange={(e) => setSelectedEnriched(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="all">All</option>
          <option value="true">Enriched</option>
          <option value="false">Not Enriched</option>
        </select>
        <select
          value={sortField}
          onChange={(e) => setSortField(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          <option value="-marketing_score">Marketing Score ↓</option>
          <option value="marketing_score">Marketing Score ↑</option>
          <option value="-created_at">Newest First</option>
          <option value="created_at">Oldest First</option>
          <option value="-fit_score">Fit Score (High→Low)</option>
          <option value="fit_score">Fit Score (Low→High)</option>
          <option value="seo_rank">SEO Rank (Best)</option>
          <option value="-review_count">Most Reviews</option>
          <option value="tier">Tier (1→3)</option>
          <option value="-tier">Tier (3→1)</option>
          <option value="name">Name (A→Z)</option>
          <option value="-name">Name (Z→A)</option>
          <option value="city">City (A→Z)</option>
          <option value="-last_contacted_at">Last Contacted</option>
        </select>
        <div className="text-sm text-gray-500 dark:text-gray-400 ml-auto">
          {filteredProspects.length} prospects
        </div>
      </div>

      {/* Main content */}
      <div className="flex gap-4">
        {/* Prospect table */}
        <div className={`flex-1 ${selectedProspect ? 'w-1/2' : 'w-full'}`}>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <div className="col-span-3">Company</div>
              <div className="col-span-2">Reviews</div>
              <div className="col-span-2">Contact / Owner</div>
              <div className="col-span-1 text-center">SEO</div>
              <div className="col-span-1 text-center">Mkt</div>
              <div className="col-span-1 text-center">Fit</div>
              <div className="col-span-1 text-center">Tier</div>
              <div className="col-span-1">Status</div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedProspects.length === 0 ? (
                <div className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                  No prospects found. Import a CSV to get started.
                </div>
              ) : (
                paginatedProspects.map(prospect => {
                  const statusInfo = getStatusInfo(prospect.status);
                  const verticalInfo = VERTICALS.find(v => v.value === prospect.vertical);
                  return (
                    <div
                      key={prospect.id}
                      onClick={() => handleSelectProspect(prospect)}
                      className={`grid grid-cols-12 gap-2 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${selectedProspect?.id === prospect.id ? 'bg-primary-50 dark:bg-primary-900/20' : ''}`}
                    >
                      <div className="col-span-3 truncate">
                        <span className="font-medium text-gray-900 dark:text-white">{prospect.company_name || prospect.name}</span>
                        {prospect.city && (
                          <span className="block text-xs text-gray-500">{prospect.city}, {prospect.state}</span>
                        )}
                      </div>
                      <div className="col-span-2 truncate text-sm text-gray-600 dark:text-gray-300">
                        {prospect.review_count ? (
                          <span className="block">
                            <span className="font-medium">{prospect.review_count}</span>
                            <span className="text-xs text-gray-400"> reviews</span>
                            {prospect.review_rating && (
                              <span className="ml-1 text-yellow-500">{prospect.review_rating}★</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                      <div className="col-span-2 truncate text-sm text-gray-600 dark:text-gray-300">
                        <span className="block">{prospect.owner_name || prospect.contact_person || '-'}</span>
                        {prospect.phone && (
                          <span className="block text-xs text-gray-500">{prospect.phone}</span>
                        )}
                      </div>
                      <div className="col-span-1 text-center text-sm">
                        {prospect.seo_rank ? (
                          <span className={`font-medium ${prospect.seo_rank <= 3 ? 'text-green-600' : prospect.seo_rank <= 10 ? 'text-blue-600' : 'text-gray-500'}`}>
                            #{prospect.seo_rank}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </div>
                      <div className={`col-span-1 text-center font-bold ${getScoreColor(prospect.marketing_score || 0)}`}>
                        {prospect.marketing_score || 0}
                      </div>
                      <div className={`col-span-1 text-center font-bold ${getScoreColor(prospect.fit_score || 0)}`}>
                        {prospect.fit_score || 0}
                      </div>
                      <div className="col-span-1 text-center">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getTierColor(prospect.tier)}`}>
                          T{prospect.tier}
                        </span>
                      </div>
                      <div className="col-span-1">
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {((currentPage - 1) * PAGE_SIZE) + 1} - {Math.min(currentPage * PAGE_SIZE, filteredProspects.length)} of {filteredProspects.length}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Detail panel */}
        {selectedProspect && (
          <div className="w-1/2 min-w-[400px]">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 sticky top-4 max-h-[calc(100vh-180px)] overflow-y-auto">
              {loadingDetail ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
                </div>
              ) : (
                <div className="p-4 space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.company_name || ''}
                          onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                          className="text-xl font-bold w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      ) : (
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">{selectedProspect.company_name || selectedProspect.name}</h2>
                      )}
                      <div className="flex items-center gap-2 mt-1">
                        {isEditing ? (
                          <select
                            value={editForm.vertical || 'other'}
                            onChange={(e) => setEditForm({ ...editForm, vertical: e.target.value })}
                            className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            {VERTICALS.map(v => (
                              <option key={v.value} value={v.value}>{v.label}</option>
                            ))}
                          </select>
                        ) : selectedProspect.vertical && (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            {VERTICALS.find(v => v.value === selectedProspect.vertical)?.label || selectedProspect.vertical}
                          </span>
                        )}
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getTierColor(selectedProspect.tier)}`}>
                          Tier {selectedProspect.tier}
                        </span>
                        <span className={`text-sm font-bold ${getScoreColor(selectedProspect.fit_score)}`}>
                          Score: {selectedProspect.fit_score}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            disabled={savingEdit}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-1"
                          >
                            {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={handleStartEdit}
                          className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50"
                        >
                          Edit
                        </button>
                      )}
                      <button onClick={() => { setSelectedProspect(null); setIsEditing(false); }} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Status & Actions */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={selectedProspect.status}
                      onChange={(e) => handleStatusChange(selectedProspect.id, e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      disabled={isEditing}
                    >
                      {STATUSES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                    {!isEditing && (
                      <>
                        {/* Enrich Button - only for new prospects */}
                        {selectedProspect.status === 'new' && (
                          <button
                            onClick={handleStartEnrichment}
                            disabled={enriching}
                            className="px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 flex items-center gap-1 disabled:opacity-50"
                          >
                            {enriching ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" /> Enriching...
                              </>
                            ) : (
                              <>
                                <Zap className="w-4 h-4" /> Enrich with AI
                              </>
                            )}
                          </button>
                        )}
                        <button
                          onClick={handleCalculateScore}
                          className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-1"
                        >
                          <Target className="w-4 h-4" /> Recalculate Score
                        </button>
                        {!selectedProspect.converted_to_deal && selectedProspect.status === 'won' && (
                          <button
                            onClick={handleConvertToDeal}
                            className="px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                          >
                            Convert to Internal Lead
                          </button>
                        )}
                        {selectedProspect.deal_id && (
                          <a
                            href={`/pipeline?deal=${selectedProspect.deal_id}`}
                            className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-1"
                          >
                            View Internal Lead <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </>
                    )}
                  </div>

                  {/* Score Reasoning */}
                  {selectedProspect.score_reason && (
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3">
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Score Breakdown</div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{selectedProspect.score_reason}</p>
                    </div>
                  )}

                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-3 text-sm border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs mb-1">
                        <User className="w-3 h-3" /> Owner Name
                      </div>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.owner_name || ''}
                          onChange={(e) => setEditForm({ ...editForm, owner_name: e.target.value })}
                          placeholder="Owner name"
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      ) : (
                        <p className="font-medium text-gray-900 dark:text-white">{selectedProspect.owner_name || selectedProspect.contact_person || '-'}</p>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs mb-1">
                        <Phone className="w-3 h-3" /> Phone
                      </div>
                      {isEditing ? (
                        <input
                          type="tel"
                          value={editForm.phone || ''}
                          onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                          placeholder="Phone"
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      ) : selectedProspect.phone ? (
                        <a href={`tel:${selectedProspect.phone}`} className="font-medium text-primary-600 hover:underline">
                          {selectedProspect.phone}
                        </a>
                      ) : (
                        <p className="text-gray-400">-</p>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs mb-1">
                        <Mail className="w-3 h-3" /> Email
                      </div>
                      {isEditing ? (
                        <input
                          type="email"
                          value={editForm.owner_email || ''}
                          onChange={(e) => setEditForm({ ...editForm, owner_email: e.target.value })}
                          placeholder="Email"
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      ) : (selectedProspect.owner_email || selectedProspect.email) ? (
                        <a href={`mailto:${selectedProspect.owner_email || selectedProspect.email}`} className="font-medium text-primary-600 hover:underline">
                          {selectedProspect.owner_email || selectedProspect.email}
                        </a>
                      ) : (
                        <p className="text-gray-400">-</p>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs mb-1">
                        <ExternalLink className="w-3 h-3" /> Website
                      </div>
                      {isEditing ? (
                        <input
                          type="url"
                          value={editForm.website || ''}
                          onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                          placeholder="https://..."
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      ) : selectedProspect.website ? (
                        <a href={selectedProspect.website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 hover:underline truncate block">
                          {selectedProspect.website.replace(/^https?:\/\//, '').substring(0, 30)}
                        </a>
                      ) : (
                        <p className="text-gray-400">-</p>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs mb-1">
                        <MapPin className="w-3 h-3" /> City
                      </div>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.city || ''}
                          onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                          placeholder="City"
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      ) : (
                        <p className="font-medium text-gray-900 dark:text-white">{selectedProspect.city || '-'}</p>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs mb-1">
                        <MapPin className="w-3 h-3" /> State
                      </div>
                      {isEditing ? (
                        <input
                          type="text"
                          value={editForm.state || ''}
                          onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                          placeholder="State"
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      ) : (
                        <p className="font-medium text-gray-900 dark:text-white">{selectedProspect.state || '-'}</p>
                      )}
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs mb-1">
                        <Building2 className="w-3 h-3" /> Google Business URL
                      </div>
                      {isEditing ? (
                        <input
                          type="url"
                          value={editForm.google_business_url || ''}
                          onChange={(e) => setEditForm({ ...editForm, google_business_url: e.target.value })}
                          placeholder="https://maps.google.com/..."
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      ) : selectedProspect.google_business_url ? (
                        <a href={selectedProspect.google_business_url} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 hover:underline truncate block">
                          View Profile
                        </a>
                      ) : (
                        <p className="text-gray-400">-</p>
                      )}
                    </div>
                    {!isEditing && selectedProspect.address_full && (
                      <div className="col-span-2">
                        <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400 text-xs mb-1">
                          <MapPin className="w-3 h-3" /> Full Address
                        </div>
                        <p className="font-medium text-gray-900 dark:text-white">{selectedProspect.address_full}</p>
                      </div>
                    )}
                  </div>

                  {/* Signals */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Signals</h3>
                    {isEditing ? (
                      <div className="grid grid-cols-2 gap-3">
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={editForm.has_financing || false}
                            onChange={(e) => setEditForm({ ...editForm, has_financing: e.target.checked })}
                            className="rounded border-gray-300"
                          />
                          <DollarSign className="w-4 h-4 text-green-600" /> Has Financing
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={editForm.has_marketing_tools || false}
                            onChange={(e) => setEditForm({ ...editForm, has_marketing_tools: e.target.checked })}
                            className="rounded border-gray-300"
                          />
                          <Zap className="w-4 h-4 text-blue-600" /> Marketing Tools
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={editForm.runs_google_ads || false}
                            onChange={(e) => setEditForm({ ...editForm, runs_google_ads: e.target.checked })}
                            className="rounded border-gray-300"
                          />
                          Google Ads
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={editForm.has_branded_fleet || false}
                            onChange={(e) => setEditForm({ ...editForm, has_branded_fleet: e.target.checked })}
                            className="rounded border-gray-300"
                          />
                          Branded Fleet
                        </label>
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={editForm.is_enterprise || false}
                            onChange={(e) => setEditForm({ ...editForm, is_enterprise: e.target.checked })}
                            className="rounded border-gray-300"
                          />
                          Enterprise
                        </label>
                        <div className="col-span-2 grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs text-gray-500">Review Count</label>
                            <input
                              type="number"
                              value={editForm.review_count || 0}
                              onChange={(e) => setEditForm({ ...editForm, review_count: parseInt(e.target.value) || 0 })}
                              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Review Rating</label>
                            <input
                              type="number"
                              step="0.1"
                              min="0"
                              max="5"
                              value={editForm.review_rating || ''}
                              onChange={(e) => setEditForm({ ...editForm, review_rating: parseFloat(e.target.value) || null })}
                              placeholder="1.0 - 5.0"
                              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-wrap gap-2">
                          {selectedProspect.has_financing && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full">
                              <DollarSign className="w-3 h-3" /> Financing
                            </span>
                          )}
                          {selectedProspect.has_marketing_tools && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                              <Zap className="w-3 h-3" /> Marketing Tools
                            </span>
                          )}
                          {selectedProspect.runs_google_ads && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded-full">
                              Google Ads
                            </span>
                          )}
                          {selectedProspect.has_branded_fleet && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 rounded-full">
                              Branded Fleet
                            </span>
                          )}
                          {selectedProspect.review_count > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 rounded-full">
                              <Star className="w-3 h-3" /> {selectedProspect.review_count} Reviews ({selectedProspect.review_rating || 'N/A'})
                            </span>
                          )}
                          {selectedProspect.is_enterprise && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300 rounded-full">
                              Enterprise
                            </span>
                          )}
                          {selectedProspect.hiring_signals && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300 rounded-full">
                              Hiring
                            </span>
                          )}
                          {selectedProspect.human_verified && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full">
                              <CheckCircle className="w-3 h-3" /> Verified
                            </span>
                          )}
                          {!selectedProspect.has_financing && !selectedProspect.has_marketing_tools && !selectedProspect.runs_google_ads &&
                           !selectedProspect.has_branded_fleet && selectedProspect.review_count === 0 && !selectedProspect.is_enterprise && (
                            <span className="text-sm text-gray-400">No signals detected - click Edit to add</span>
                          )}
                        </div>
                        {selectedProspect.tools_detected && selectedProspect.tools_detected.length > 0 && (
                          <div className="mt-2 text-xs text-gray-500">
                            Tools: {selectedProspect.tools_detected.join(', ')}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Touch Log */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Touches ({selectedProspect.touches?.length || 0})</h3>
                    <form onSubmit={handleAddTouch} className="flex gap-2 mb-3">
                      <select
                        value={newTouchChannel}
                        onChange={(e) => setNewTouchChannel(e.target.value)}
                        className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {TOUCH_CHANNELS.map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                      <select
                        value={newTouchResult}
                        onChange={(e) => setNewTouchResult(e.target.value)}
                        className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {TOUCH_RESULTS.map(r => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={newTouchNotes}
                        onChange={(e) => setNewTouchNotes(e.target.value)}
                        placeholder="Notes..."
                        className="flex-1 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <button
                        type="submit"
                        disabled={savingTouch}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
                      >
                        {savingTouch ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Log'}
                      </button>
                    </form>
                    {selectedProspect.touches && selectedProspect.touches.length > 0 && (
                      <div className="space-y-2 max-h-32 overflow-y-auto">
                        {selectedProspect.touches.map((touch, idx) => {
                          const channelInfo = TOUCH_CHANNELS.find(c => c.value === touch.channel);
                          const resultInfo = TOUCH_RESULTS.find(r => r.value === touch.result);
                          return (
                            <div key={idx} className="flex items-center gap-2 text-sm bg-gray-50 dark:bg-gray-900 rounded px-2 py-1.5">
                              <span className="font-medium">{channelInfo?.label || touch.channel}</span>
                              <span className="text-gray-500">→</span>
                              <span className={`${touch.result === 'booked' ? 'text-green-600' : touch.result === 'replied' ? 'text-blue-600' : 'text-gray-600'}`}>
                                {resultInfo?.label || touch.result}
                              </span>
                              {touch.notes && <span className="text-gray-500 truncate flex-1">{touch.notes}</span>}
                              <span className="text-xs text-gray-400">{new Date(touch.sent_at).toLocaleDateString()}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  {selectedProspect.description && (
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Description</div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{selectedProspect.description}</p>
                    </div>
                  )}

                  {/* Notes */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Notes</div>
                    <textarea
                      value={selectedProspect.notes || ''}
                      onChange={async (e) => {
                        const newNotes = e.target.value;
                        setSelectedProspect({ ...selectedProspect, notes: newNotes });
                      }}
                      onBlur={async () => {
                        try {
                          await api.updateProspect(selectedProspect.id, { notes: selectedProspect.notes });
                        } catch (err) {
                          console.error('Failed to save notes:', err);
                        }
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                      rows={3}
                      placeholder="Add notes..."
                    />
                  </div>

                  {/* Activity Log */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Activity Log</h3>
                    <form onSubmit={handleAddActivity} className="flex gap-2 mb-3">
                      <select
                        value={newActivityType}
                        onChange={(e) => setNewActivityType(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {ACTIVITY_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        value={newActivityDescription}
                        onChange={(e) => setNewActivityDescription(e.target.value)}
                        placeholder="What happened?"
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <button
                        type="submit"
                        disabled={savingActivity || !newActivityDescription.trim()}
                        className="px-3 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
                      >
                        {savingActivity ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                      </button>
                    </form>

                    {/* Activity Timeline */}
                    {selectedProspect.activities && selectedProspect.activities.length > 0 ? (
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {selectedProspect.activities.map((activity, idx) => {
                          const activityInfo = ACTIVITY_TYPES.find(t => t.value === activity.activity_type);
                          const Icon = activityInfo?.icon || MessageSquare;
                          return (
                            <div key={idx} className="flex gap-3 text-sm">
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                <Icon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-gray-900 dark:text-white">{activity.description}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(activity.created_at).toLocaleString()}
                                  {activity.user_name && ` by ${activity.user_name}`}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No activities yet</p>
                    )}
                  </div>

                  {/* Contacts Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Users className="w-4 h-4" /> Contacts ({prospectContacts.length})
                      </h3>
                      <div className="flex items-center gap-2">
                        {prospectContacts.some(c => c.email) && (
                          <button
                            onClick={handleOpenEnrollModal}
                            className="px-2 py-1 text-sm font-medium text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                            title="Add contacts to email campaign"
                          >
                            <Mail className="w-4 h-4" /> Campaign
                          </button>
                        )}
                        <button
                          onClick={() => setShowAddContactForm(!showAddContactForm)}
                          className="px-2 py-1 text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
                        >
                          <UserPlus className="w-4 h-4" /> Add
                        </button>
                      </div>
                    </div>

                    {showAddContactForm && (
                      <form onSubmit={handleAddContact} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 mb-3 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={newContact.first_name}
                            onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })}
                            placeholder="First name *"
                            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            required
                          />
                          <input
                            type="text"
                            value={newContact.last_name}
                            onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })}
                            placeholder="Last name"
                            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={newContact.title}
                            onChange={(e) => setNewContact({ ...newContact, title: e.target.value })}
                            placeholder="Title (e.g., Owner)"
                            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <select
                            value={newContact.title_category}
                            onChange={(e) => setNewContact({ ...newContact, title_category: e.target.value })}
                            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          >
                            <option value="owner">Owner</option>
                            <option value="marketing">Marketing</option>
                            <option value="sales">Sales</option>
                            <option value="operations">Operations</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="email"
                            value={newContact.email}
                            onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                            placeholder="Email"
                            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <input
                            type="tel"
                            value={newContact.phone}
                            onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                            placeholder="Phone"
                            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <input
                          type="url"
                          value={newContact.linkedin_url}
                          onChange={(e) => setNewContact({ ...newContact, linkedin_url: e.target.value })}
                          placeholder="LinkedIn URL"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                        <div className="flex items-center justify-between">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={newContact.is_decision_maker}
                              onChange={(e) => setNewContact({ ...newContact, is_decision_maker: e.target.checked })}
                              className="rounded border-gray-300"
                            />
                            Decision Maker
                          </label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setShowAddContactForm(false)}
                              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={savingContact || !newContact.first_name.trim()}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-primary-500 rounded hover:bg-primary-600 disabled:opacity-50"
                            >
                              {savingContact ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                            </button>
                          </div>
                        </div>
                      </form>
                    )}

                    {loadingContacts ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                      </div>
                    ) : prospectContacts.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {prospectContacts.map((contact) => (
                          <div key={contact.id} className="flex items-start gap-3 bg-gray-50 dark:bg-gray-900 rounded-lg p-2">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                              <User className="w-4 h-4 text-primary-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900 dark:text-white text-sm">
                                  {contact.full_name}
                                </span>
                                {contact.is_primary && (
                                  <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">Primary</span>
                                )}
                                {contact.is_decision_maker && (
                                  <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">DM</span>
                                )}
                              </div>
                              {contact.title && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">{contact.title}</p>
                              )}
                              <div className="flex items-center gap-3 mt-1 text-xs">
                                {contact.email && (
                                  <a href={`mailto:${contact.email}`} className="text-primary-600 hover:underline flex items-center gap-1">
                                    <Mail className="w-3 h-3" /> {contact.email}
                                  </a>
                                )}
                                {contact.phone && (
                                  <a href={`tel:${contact.phone}`} className="text-primary-600 hover:underline flex items-center gap-1">
                                    <Phone className="w-3 h-3" /> {contact.phone}
                                  </a>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {!contact.is_primary && (
                                <button
                                  onClick={() => handleSetPrimaryContact(contact.id)}
                                  className="p-1 text-gray-400 hover:text-green-600"
                                  title="Set as primary"
                                >
                                  <Star className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteContact(contact.id)}
                                className="p-1 text-gray-400 hover:text-red-600"
                                title="Delete contact"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No contacts yet. Add decision makers to track outreach.</p>
                    )}
                  </div>

                  {/* Trackable Links Section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <Link className="w-4 h-4" /> Trackable Links ({prospectLinks.length})
                      </h3>
                      <button
                        onClick={() => setShowAddLinkForm(!showAddLinkForm)}
                        className="px-2 py-1 text-sm font-medium text-primary-600 hover:text-primary-700 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" /> Add
                      </button>
                    </div>

                    {showAddLinkForm && (
                      <form onSubmit={handleAddLink} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 mb-3 space-y-2">
                        <input
                          type="url"
                          value={newLink.target_url}
                          onChange={(e) => setNewLink({ ...newLink, target_url: e.target.value })}
                          placeholder="Target URL (e.g., https://digimint.com/report.pdf) *"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          required
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            value={newLink.campaign}
                            onChange={(e) => setNewLink({ ...newLink, campaign: e.target.value })}
                            placeholder="Campaign (e.g., outreach)"
                            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                          <input
                            type="text"
                            value={newLink.notes}
                            onChange={(e) => setNewLink({ ...newLink, notes: e.target.value })}
                            placeholder="Notes"
                            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setShowAddLinkForm(false)}
                            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={savingLink || !newLink.target_url.trim()}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-500 rounded hover:bg-primary-600 disabled:opacity-50"
                          >
                            {savingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Link'}
                          </button>
                        </div>
                      </form>
                    )}

                    {loadingLinks ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                      </div>
                    ) : prospectLinks.length > 0 ? (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {prospectLinks.map((link) => (
                          <div key={link.id} className="bg-gray-50 dark:bg-gray-900 rounded-lg p-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <Link className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <code className="text-xs text-primary-600 bg-primary-50 dark:bg-primary-900/20 px-1.5 py-0.5 rounded truncate">
                                  {link.short_url}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(link.short_url)}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                  title="Copy link"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                              <div className="flex items-center gap-2 text-xs">
                                <span className={`flex items-center gap-1 ${link.click_count > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                                  <MousePointerClick className="w-3 h-3" />
                                  {link.click_count} {link.click_count === 1 ? 'click' : 'clicks'}
                                </span>
                              </div>
                            </div>
                            <div className="mt-1 text-xs text-gray-500 truncate">
                              → {link.target_url}
                            </div>
                            {link.click_count > 0 && link.last_clicked_at && (
                              <div className="mt-1 text-xs text-green-600">
                                Last clicked: {new Date(link.last_clicked_at).toLocaleString()}
                              </div>
                            )}
                            {link.campaign && (
                              <span className="inline-block mt-1 px-1.5 py-0.5 text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                                {link.campaign}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No links yet. Create trackable links to monitor engagement.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Import Prospects</h2>
              <button onClick={() => { setShowImportModal(false); setImportStatus(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {importStatus?.status === 'completed' ? (
              <div className="text-center py-4">
                <div className="text-green-600 text-4xl mb-2">✓</div>
                <p className="font-medium text-gray-900 dark:text-white">Import Complete!</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Created: {importStatus.created_count} | Skipped: {importStatus.skipped_count} | Errors: {importStatus.error_count}
                </p>
                <button
                  onClick={() => { setShowImportModal(false); setImportStatus(null); }}
                  className="mt-4 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600"
                >
                  Done
                </button>
              </div>
            ) : importStatus?.status === 'processing' ? (
              <div className="text-center py-4">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-2" />
                <p className="text-gray-600 dark:text-gray-300">Processing {importStatus.processed_rows || 0} of {importStatus.total_rows} rows...</p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Select List
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={importListId}
                        onChange={(e) => setImportListId(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Select a list...</option>
                        {prospectLists.map(list => (
                          <option key={list.id} value={list.id}>{list.name}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => setShowNewListModal(true)}
                        className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      CSV File
                    </label>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    onClick={() => setShowImportModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleImport}
                    disabled={!importFile || !importListId || importing}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
                  >
                    {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    Import
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* New List Modal */}
      {showNewListModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Create New List</h2>
              <button onClick={() => setShowNewListModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateList} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  List Name
                </label>
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="e.g., JaxChamber Members 2026"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Source
                </label>
                <input
                  type="text"
                  value={newListSource}
                  onChange={(e) => setNewListSource(e.target.value)}
                  placeholder="e.g., jaxchamber"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowNewListModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* New Prospect Modal */}
      {showNewProspectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Add New Prospect</h2>
              <button onClick={() => setShowNewProspectModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateProspect} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Prospect List *
                </label>
                <div className="flex gap-2">
                  <select
                    value={newProspect.prospect_list}
                    onChange={(e) => setNewProspect({ ...newProspect, prospect_list: e.target.value })}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  >
                    <option value="">{prospectLists.length === 0 ? 'No lists yet - create one →' : 'Select a list...'}</option>
                    {prospectLists.map(list => (
                      <option key={list.id} value={list.id}>{list.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewProspectModal(false);
                      setShowNewListModal(true);
                    }}
                    className="px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-1"
                    title="Create new list"
                  >
                    <Plus className="w-4 h-4" /> List
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={newProspect.company_name}
                    onChange={(e) => setNewProspect({ ...newProspect, company_name: e.target.value })}
                    placeholder="e.g., ABC Roofing Co."
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Vertical
                  </label>
                  <select
                    value={newProspect.vertical}
                    onChange={(e) => setNewProspect({ ...newProspect, vertical: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {VERTICALS.map(v => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={newProspect.phone}
                    onChange={(e) => setNewProspect({ ...newProspect, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Owner Name
                  </label>
                  <input
                    type="text"
                    value={newProspect.owner_name}
                    onChange={(e) => setNewProspect({ ...newProspect, owner_name: e.target.value })}
                    placeholder="John Smith"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Owner Email
                  </label>
                  <input
                    type="email"
                    value={newProspect.owner_email}
                    onChange={(e) => setNewProspect({ ...newProspect, owner_email: e.target.value })}
                    placeholder="john@company.com"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    value={newProspect.website}
                    onChange={(e) => setNewProspect({ ...newProspect, website: e.target.value })}
                    placeholder="https://www.company.com"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={newProspect.city}
                    onChange={(e) => setNewProspect({ ...newProspect, city: e.target.value })}
                    placeholder="Jacksonville"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={newProspect.state}
                    onChange={(e) => setNewProspect({ ...newProspect, state: e.target.value })}
                    placeholder="FL"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={newProspect.notes}
                    onChange={(e) => setNewProspect({ ...newProspect, notes: e.target.value })}
                    placeholder="Any additional notes..."
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowNewProspectModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingProspect || !newProspect.prospect_list || !newProspect.company_name.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50 flex items-center gap-2"
                >
                  {savingProspect ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  Add Prospect
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enrichment Review Modal */}
      {showEnrichmentReview && enrichmentJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Review Enrichment Results</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedProspect?.company_name || selectedProspect?.name}
                </p>
              </div>
              <button
                onClick={() => setShowEnrichmentReview(false)}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* AI Confidence */}
              <div className="flex items-center gap-3 p-4 rounded-lg bg-gray-50 dark:bg-gray-900">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                  enrichmentReviewData.confidence === 'high' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                  enrichmentReviewData.confidence === 'medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                  'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                }`}>
                  {enrichmentReviewData.confidence?.toUpperCase() || 'UNKNOWN'} Confidence
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 flex-1">
                  {enrichmentReviewData.score_reason || 'No assessment provided'}
                </p>
              </div>

              {/* Company Info */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Company Info</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Google Business URL</label>
                    <input
                      type="url"
                      value={enrichmentReviewData.google_business_url || ''}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, google_business_url: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="https://maps.google.com/..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Website</label>
                    <input
                      type="url"
                      value={enrichmentReviewData.website || ''}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, website: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Owner Name</label>
                    <input
                      type="text"
                      value={enrichmentReviewData.owner_name || ''}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, owner_name: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={enrichmentReviewData.phone || ''}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, phone: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Reviews */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Reviews & Reputation</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Rating</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        step="0.1"
                        min="1"
                        max="5"
                        value={enrichmentReviewData.review_rating || ''}
                        onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, review_rating: e.target.value ? parseFloat(e.target.value) : null })}
                        className="w-20 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="4.8"
                      />
                      <Star className="w-4 h-4 text-yellow-500" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Review Count</label>
                    <input
                      type="number"
                      value={enrichmentReviewData.review_count || ''}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, review_count: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="127"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">90-Day Velocity</label>
                    <input
                      type="number"
                      value={enrichmentReviewData.review_velocity_90d || ''}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, review_velocity_90d: e.target.value ? parseInt(e.target.value) : null })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="15"
                    />
                  </div>
                </div>
              </div>

              {/* Signals */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">Business Signals</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enrichmentReviewData.runs_google_ads || false}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, runs_google_ads: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Runs Google Ads</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enrichmentReviewData.has_marketing_tools || false}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, has_marketing_tools: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Marketing Tools</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enrichmentReviewData.has_branded_fleet || false}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, has_branded_fleet: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Branded Fleet</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enrichmentReviewData.hiring_signals || false}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, hiring_signals: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Currently Hiring</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enrichmentReviewData.has_financing || false}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, has_financing: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Offers Financing</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enrichmentReviewData.sells_high_ticket || false}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, sells_high_ticket: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">High-Ticket Services</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enrichmentReviewData.is_enterprise || false}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, is_enterprise: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Multi-Location</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={enrichmentReviewData.owner_visible || false}
                      onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, owner_visible: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Owner Visible</span>
                  </label>
                </div>
              </div>

              {/* Ads Evidence */}
              {enrichmentReviewData.ads_evidence && (
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Ads Evidence</label>
                  <textarea
                    value={enrichmentReviewData.ads_evidence || ''}
                    onChange={(e) => setEnrichmentReviewData({ ...enrichmentReviewData, ads_evidence: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  />
                </div>
              )}

              {/* Tools Detected */}
              {enrichmentReviewData.tools_detected && enrichmentReviewData.tools_detected.length > 0 && (
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Tools Detected</label>
                  <div className="flex flex-wrap gap-2">
                    {enrichmentReviewData.tools_detected.map((tool, i) => (
                      <span key={i} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Certifications */}
              {enrichmentReviewData.certifications && enrichmentReviewData.certifications.length > 0 && (
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Certifications</label>
                  <div className="flex flex-wrap gap-2">
                    {enrichmentReviewData.certifications.map((cert, i) => (
                      <span key={i} className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 rounded-full">
                        <Award className="w-3 h-3 inline mr-1" />
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Error display if present */}
              {enrichmentReviewData.error && (
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    <strong>Error:</strong> {enrichmentReviewData.error}
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={handleRejectEnrichment}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50"
              >
                Reject
              </button>
              <button
                onClick={handleApproveEnrichment}
                disabled={approvingEnrichment}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {approvingEnrichment ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                Approve & Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Campaign Enrollment Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-cyan-600" />
                Add to Email Campaign
              </h2>
              <button onClick={() => setShowEnrollModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Prospect Info */}
              <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <p className="font-medium text-gray-900 dark:text-white">
                  {selectedProspect?.company_name || selectedProspect?.name}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {prospectContacts.filter(c => c.email).length} contacts with email addresses
                </p>
              </div>

              {/* Campaign Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Select Campaign
                </label>
                <select
                  value={selectedCampaignId}
                  onChange={(e) => setSelectedCampaignId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Choose a campaign...</option>
                  {emailCampaigns.filter(c => c.status === 'active' || c.status === 'draft').map(campaign => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.name} ({campaign.status}) - {campaign.total_leads} leads
                    </option>
                  ))}
                </select>
                {emailCampaigns.length === 0 && (
                  <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                    No campaigns available. Create one in the Email Campaigns page first.
                  </p>
                )}
              </div>

              {/* Contact Selection */}
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enrollOnlyDecisionMakers}
                    onChange={(e) => setEnrollOnlyDecisionMakers(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Only enroll decision makers
                  </span>
                </label>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {enrollOnlyDecisionMakers
                    ? `${prospectContacts.filter(c => c.is_decision_maker && c.email).length} decision makers will be enrolled`
                    : `${prospectContacts.filter(c => c.email).length} contacts will be enrolled`
                  }
                </p>
              </div>

              {/* Contacts Preview */}
              <div className="max-h-32 overflow-y-auto space-y-1">
                {prospectContacts
                  .filter(c => c.email && (enrollOnlyDecisionMakers ? c.is_decision_maker : true))
                  .map(contact => (
                    <div key={contact.id} className="flex items-center gap-2 text-sm p-2 bg-gray-50 dark:bg-gray-900 rounded">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-gray-900 dark:text-white">{contact.full_name}</span>
                      <span className="text-gray-500 dark:text-gray-400">- {contact.email}</span>
                    </div>
                  ))
                }
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowEnrollModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEnrollInCampaign}
                disabled={enrolling || !selectedCampaignId || prospectContacts.filter(c => c.email && (enrollOnlyDecisionMakers ? c.is_decision_maker : true)).length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-lg hover:bg-cyan-700 disabled:opacity-50 flex items-center gap-2"
              >
                {enrolling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Enroll Contacts
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scrap.io Google Maps Import Modal */}
      {showScrapioModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-green-600" />
                  Google Maps Import
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Import businesses directly from Google Maps via Scrap.io
                </p>
              </div>
              <button onClick={() => { setShowScrapioModal(false); setScrapioImportStatus(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Credits display */}
            {scrapioCredits !== null && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-between">
                <span className="text-sm text-blue-700 dark:text-blue-300">
                  Credits Remaining: <span className="font-semibold">{scrapioCredits.toLocaleString()}</span>
                </span>
                <span className="text-xs text-blue-600 dark:text-blue-400">
                  Each result uses 1 credit
                </span>
              </div>
            )}

            {scrapioImportStatus?.status === 'completed' ? (
              <div className="text-center py-8">
                <div className="text-green-600 text-5xl mb-4">✓</div>
                <p className="font-semibold text-xl text-gray-900 dark:text-white">Import Complete!</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Created: {scrapioImportStatus.created_count} | Skipped: {scrapioImportStatus.skipped_count} | Errors: {scrapioImportStatus.error_count}
                </p>
                <button
                  onClick={() => { setShowScrapioModal(false); setScrapioImportStatus(null); }}
                  className="mt-6 px-6 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600"
                >
                  Done
                </button>
              </div>
            ) : scrapioImportStatus?.status === 'processing' ? (
              <div className="text-center py-8">
                <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
                <p className="font-medium text-gray-900 dark:text-white">Importing from Google Maps...</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Processed: {scrapioImportStatus.processed_rows || 0} | Created: {scrapioImportStatus.created_count || 0}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                  This may take a few minutes for large datasets
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-6">
                  {/* Left column - Search parameters */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-gray-900 dark:text-white">Search Parameters</h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Business Type *
                      </label>
                      <select
                        value={scrapioBusinessType}
                        onChange={(e) => setScrapioBusinessType(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        {Object.entries(scrapioBusinessTypes).length > 0 ? (
                          Object.entries(scrapioBusinessTypes).map(([key, value]) => (
                            <option key={key} value={value}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                          ))
                        ) : (
                          <>
                            <option value="roofing-contractor">Roofing</option>
                            <option value="hvac-contractor">HVAC</option>
                            <option value="plumber">Plumbing</option>
                            <option value="electrician">Electrical</option>
                            <option value="painter">Painting</option>
                            <option value="general-contractor">General Contractor</option>
                          </>
                        )}
                      </select>
                    </div>

                    {/* Import Mode Toggle */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Import Mode
                      </label>
                      <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setScrapioImportMode('city')}
                          className={`flex-1 px-4 py-2 text-sm font-medium ${scrapioImportMode === 'city' ? 'bg-green-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}
                        >
                          Single City
                        </button>
                        <button
                          type="button"
                          onClick={() => setScrapioImportMode('state')}
                          className={`flex-1 px-4 py-2 text-sm font-medium ${scrapioImportMode === 'state' ? 'bg-green-600 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'}`}
                        >
                          Entire State
                        </button>
                      </div>
                    </div>

                    {scrapioImportMode === 'city' ? (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            City *
                          </label>
                          <input
                            type="text"
                            value={scrapioCity}
                            onChange={(e) => { setScrapioCity(e.target.value); setScrapioCityError(''); }}
                            placeholder="e.g., Jacksonville"
                            className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${scrapioCityError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                          />
                          {scrapioCityError && (
                            <p className="mt-1 text-xs text-red-500">{scrapioCityError}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Max Results
                          </label>
                          <input
                            type="number"
                            value={scrapioMaxResults}
                            onChange={(e) => setScrapioMaxResults(e.target.value)}
                            placeholder="100"
                            min="1"
                            max="1000"
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            State *
                          </label>
                          <select
                            value={scrapioStateCode}
                            onChange={(e) => { setScrapioStateCode(e.target.value); setScrapioCityError(''); }}
                            className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${scrapioCityError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'}`}
                          >
                            <option value="">Select a state...</option>
                            {scrapioUsStates.map(state => (
                              <option key={state.code} value={state.code}>
                                {state.name} ({state.city_count} cities)
                              </option>
                            ))}
                          </select>
                          {scrapioCityError && (
                            <p className="mt-1 text-xs text-red-500">{scrapioCityError}</p>
                          )}
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Searches all major cities in the state
                          </p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Max per City
                            </label>
                            <input
                              type="number"
                              value={scrapioMaxResultsPerCity}
                              onChange={(e) => setScrapioMaxResultsPerCity(e.target.value)}
                              placeholder="50"
                              min="1"
                              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Max Cities
                            </label>
                            <input
                              type="number"
                              value={scrapioMaxCities}
                              onChange={(e) => setScrapioMaxCities(e.target.value)}
                              placeholder="All"
                              min="1"
                              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          State imports search each city separately and may use many credits.
                        </p>
                      </>
                    )}
                  </div>

                  {/* Right column - Import settings */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-gray-900 dark:text-white">Import Settings</h3>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Import to List
                      </label>
                      <select
                        value={scrapioListId}
                        onChange={(e) => { setScrapioListId(e.target.value); setScrapioListName(''); }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="">Create new list...</option>
                        {prospectLists.map(list => (
                          <option key={list.id} value={list.id}>{list.name}</option>
                        ))}
                      </select>
                    </div>

                    {!scrapioListId && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          New List Name *
                        </label>
                        <input
                          type="text"
                          value={scrapioListName}
                          onChange={(e) => setScrapioListName(e.target.value)}
                          placeholder={`e.g., ${scrapioImportMode === 'state' ? (scrapioStateCode || 'State') : (scrapioCity || 'City')} ${scrapioBusinessType.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    )}

                    {/* Info box */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
                      <h4 className="text-sm font-medium text-gray-900 dark:text-white">What you'll get:</h4>
                      <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        <li>• Business name and address</li>
                        <li>• Phone number and website</li>
                        <li>• Google rating and review count</li>
                        <li>• Owner name and email (when available)</li>
                        <li>• Social media links</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => { setShowScrapioModal(false); }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleScrapioImport}
                    disabled={scrapioImporting || (scrapioImportMode === 'city' ? !scrapioCity.trim() : !scrapioStateCode) || (!scrapioListId && !scrapioListName)}
                    className="px-6 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {scrapioImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                    {scrapioImportMode === 'state' ? 'Start State Import' : 'Start Import'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProspectsPage;
